import { Alert, Box, Tab, Tabs } from '@mui/material';

import {
  ConfirmationDialog,
  FormTitleBox,
} from '../../common/components';
import AccountingDashboardTab from './components/AccountingDashboardTab';
import AccountingEntriesTable from './components/AccountingEntriesTable';
import AccountingEntriesToolbar from './components/AccountingEntriesToolbar';
import AccountingEntryDetailDrawer from './components/AccountingEntryDetailDrawer';
import AccountingEntryDrawer from './components/AccountingEntryDrawer';
import AccountingLedgerTable from './components/AccountingLedgerTable';
import useAccountingEntriesController from './hooks/useAccountingEntriesController';

const AccountingEntries = () => {
  const {
    activeTab,
    cancelEntry,
    clearError,
    closeCancelDialog,
    closeEditor,
    closePostDialog,
    closeViewer,
    editingEntry,
    entries,
    entryPagination,
    error,
    filters,
    handleCancel,
    handleFilterChange,
    handlePost,
    isCancelling,
    isLoading,
    isPosting,
    isSaving,
    ledgerRows,
    openEditor,
    openViewer,
    postEntry,
    saveEntry,
    setActiveTab,
    setCancelEntry,
    setPostEntry,
    viewEntry,
  } = useAccountingEntriesController();

  return (
    <Box>
      <FormTitleBox title="Finanzas | Asientos contables" />

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ backgroundColor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44,
              fontSize: 13,
              fontWeight: 700,
            },
          }}
        >
          <Tab value="entries" label="Asientos" />
          <Tab value="ledger" label="Mayor por cuenta" />
          <Tab value="dashboard" label="Tableros" />
        </Tabs>
      </Box>

      {activeTab === 'entries' && (
        <>
          <AccountingEntriesToolbar
            filters={filters}
            onFilterChange={handleFilterChange}
            onCreate={() => openEditor({})}
          />
          <AccountingEntriesTable
            entries={entries}
            isLoading={isLoading}
            pagination={entryPagination}
            onView={openViewer}
            onEdit={openEditor}
            onPost={setPostEntry}
            onCancel={setCancelEntry}
          />
        </>
      )}

      {activeTab === 'ledger' && (
        <AccountingLedgerTable rows={ledgerRows} isLoading={isLoading} />
      )}

      {activeTab === 'dashboard' && (
        <AccountingDashboardTab entries={entries} ledgerRows={ledgerRows} loading={isLoading} />
      )}

      <AccountingEntryDrawer
        open={Boolean(editingEntry)}
        entry={editingEntry?.uuid ? editingEntry : null}
        onClose={closeEditor}
        onSave={saveEntry}
        isSaving={isSaving}
      />
      <AccountingEntryDetailDrawer entry={viewEntry} onClose={closeViewer} />
      <ConfirmationDialog
        open={Boolean(postEntry)}
        title="Contabilizar asiento"
        message={`¿Querés contabilizar el asiento #${postEntry?.entry_number || ''}? Después no se podrá editar.`}
        confirmText="Contabilizar"
        confirmVariant="base"
        onClose={closePostDialog}
        onConfirm={handlePost}
        isLoading={isPosting}
      />
      <ConfirmationDialog
        open={Boolean(cancelEntry)}
        title="Anular asiento"
        message={`¿Querés anular el asiento #${cancelEntry?.entry_number || ''}? Quedará visible como anulado.`}
        confirmText="Anular"
        confirmVariant="danger"
        onClose={closeCancelDialog}
        onConfirm={handleCancel}
        isLoading={isCancelling}
      />
    </Box>
  );
};

export default AccountingEntries;
